import { CheckCircle2, XCircle, AlertCircle, FileText, Lightbulb } from 'lucide-react'

const isTimeoutError = (message) => {
  const msg = String(message || '').toLowerCase()
  return msg.includes('tiempo de espera') || msg.includes('timeout')
}

const formatErrorMessage = (message) => {
  if (isTimeoutError(message)) {
    return 'Rechazado por el SII'
  }
  return message || 'Error desconocido al emitir el documento.'
}

export default function ReporteEmisionMasiva({ results }) {
  const successCount = results.filter(r => r.status === 'success').length
  const errorCount = results.filter(r => r.status === 'error').length
  const totalCount = results.length

  const errorResults = results.filter(r => r.status === 'error')

  const styles = {
    table: 'w-full table-fixed border-collapse font-sans text-xs leading-tight',
    thead: 'sticky top-0 z-10 bg-white',
    th: 'py-2.5 px-1 sm:px-2 w-auto text-center break-words border-b border-gray-200 uppercase text-[0.6rem] font-bold text-gray-500',
    tr: 'border-b border-gray-50 hover:bg-gray-50/50 transition-colors',
    td: 'px-1 py-3 align-middle sm:px-2 md:px-2.5 xl:px-3 text-center',
    boldText: 'font-bold text-[0.66rem] text-slate-900',
    text: 'text-[0.64rem] font-medium text-slate-500'
  }

  return (
    <div className='flex flex-col flex-1 min-h-0 bg-white p-4 animate-in fade-in duration-500 overflow-hidden'>
      {/* Header Cards */}
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-3'>
        {/* Total Card */}
        <div className='group relative bg-slate-50 border border-slate-200 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-slate-300'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-slate-500'>Total Procesadas</p>
            <p className='text-4xl font-black text-slate-800 mt-1'>{totalCount}</p>
          </div>
          <FileText className='absolute right-[-10px] bottom-[-10px] text-slate-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>

        {/* Success Card */}
        <div className='group relative bg-emerald-50 border border-emerald-100 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-emerald-200'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-emerald-600'>Emitidas con Éxito</p>
            <p className='text-4xl font-black text-emerald-700 mt-1'>{successCount}</p>
          </div>
          <CheckCircle2 className='absolute right-[-10px] bottom-[-10px] text-emerald-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>

        {/* Error Card */}
        <div className='group relative bg-rose-50 border border-rose-100 rounded-2xl p-3 overflow-hidden transition-all hover:shadow-lg hover:border-rose-200'>
          <div className='relative z-10'>
            <p className='text-[11px] font-bold uppercase tracking-wider text-rose-600'>Rechazadas o Fallidas</p>
            <p className='text-4xl font-black text-rose-700 mt-1'>{errorCount}</p>
          </div>
          <XCircle className='absolute right-[-10px] bottom-[-10px] text-rose-200 opacity-50 transition-transform group-hover:scale-110 group-hover:rotate-6 duration-500' size={100} />
        </div>
      </div>

      {errorCount > 0 ? (
        <div className='flex-1 min-h-full flex flex-col'>
          <div className='mb-3'>
            <h3 className='font-bold text-slate-900 text-xl flex items-center gap-2.5'>
              <AlertCircle className='text-rose-500' size={24} />
              Documentos rechazados
            </h3>
            <p className='text-slate-500 text-sm mt-1 ml-8 font-medium'>
              Estos documentos no pudieron emitirse. Revisa el motivo del error para corregirlos.
            </p>
          </div>

          <div className='overflow-hidden border border-slate-200 rounded-2xl shadow-sm flex-1 min-h-[200px] flex flex-col'>
            <div className='overflow-auto flex-1 custom-scrollbar'>
              <table className={styles.table}>
                <thead className='bg-slate-50/80 backdrop-blur-sm border-b border-slate-200'>
                  <tr>
                    <th className={`${styles.th} w-[10%] text-slate-600`}>Fila</th>
                    <th className={`${styles.th} w-[20%] text-slate-600`}>RUT</th>
                    <th className={`${styles.th} w-[35%] text-slate-600`}>Razón Social</th>
                    <th className={`${styles.th} w-[35%] text-slate-600`}>Motivo del Error</th>
                  </tr>
                </thead>
                <tbody className='bg-white'>
                  {errorResults.map((err, idx) => (
                    <tr key={idx} className={styles.tr}>
                      <td className={styles.td}>
                        <span className='inline-flex items-center justify-center h-6 w-8 bg-slate-100 rounded text-[10px] font-bold text-slate-500'>
                          {err.index + 1}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className={styles.boldText}>{err.doc.document_receiver?.rut}</span>
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.text} truncate block max-w-[200px] mx-auto`}>
                          {err.doc.document_receiver?.business_name || 'N/A'}
                        </span>
                      </td>
                      <td className={styles.td}>
                        <span className='inline-flex items-center justify-center text-rose-600 text-[0.62rem] font-semibold bg-rose-50 px-3 py-1.5 rounded-full border border-rose-100 shadow-sm'>
                          {formatErrorMessage(err.message)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Next Step Informative Box */}
          <div className='mt-4 flex items-start gap-4 bg-slate-50 border border-slate-200 rounded-2xl p-3'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-sm'>
              <Lightbulb size={20} />
            </div>
            <div>
              <h4 className='font-bold text-slate-900 text-sm'>Próximo paso</h4>
              <p className='text-slate-600 text-[13px] leading-relaxed'>
                Corrige los documentos rechazados en tu archivo Excel y vuelve a intentar la carga solo con esos registros.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className='flex-1 flex flex-col items-center justify-center p-14 mt-4 bg-emerald-50/30 border border-emerald-100 border-dashed rounded-[32px]'>
          <div className='h-20 w-20 bg-emerald-100 rounded-3xl flex items-center justify-center text-emerald-600 mb-6 shadow-xl shadow-emerald-200/50'>
            <CheckCircle2 size={40} />
          </div>
          <h3 className='text-2xl font-black text-slate-900 mb-3'>¡Excelente trabajo!</h3>
          <p className='text-slate-500 text-center max-w-sm font-medium leading-relaxed'>
            Todos los documentos del lote fueron emitidos exitosamente en el SII. No hay errores que reportar.
          </p>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
          height: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f8fafc;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #cbd5e1;
        }
      `}</style>
    </div>
  )
}
